// ScreenshotDialog.jsx
// Contains dialog UI and logic to preview and configure snapshot options before saving in the HSA-KIT File Viewer.
//
// Copyright Viktor Eberhardt, 2025
// Author: Viktor Eberhardt

// Framework imports
import React from "react";
import PropTypes from "prop-types";

// MUI imports (single-line)
import { makeStyles } from "@mui/styles";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Tooltip from "@mui/material/Tooltip";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faRuler,
  faQrcode,
  faInfo,
  faUser,
  faClock,
} from "@fortawesome/free-solid-svg-icons";

// HSA imports
import { FileViewerContext } from "../FileViewer";
import Backend from "../../common/utils/Backend";
import CornerButton from "./CornerButton";
import { cropCanvasToNonBlack, captureCanvasClone } from "./canvasUtils";
import { drawOverlaysStacked } from "./overlayUtils";

const SS_SETTINGS_KEY = "hsaFileViewerScreenshotSettings";

const useStyles = makeStyles(() => ({
  previewBox: {
    width: "100%",
    maxHeight: 480,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "auto",
    backgroundColor: "#eaeaea",
    backgroundImage:
      "linear-gradient(45deg, #f5f5f5 25%, transparent 25%)," +
      "linear-gradient(-45deg, #f5f5f5 25%, transparent 25%)," +
      "linear-gradient(45deg, transparent 75%, #f5f5f5 75%)," +
      "linear-gradient(-45deg, transparent 75%, #f5f5f5 75%)",
    backgroundSize: "20px 20px",
    backgroundPosition: "0 0, 0 10px, 10px -10px, -10px 0px",
    borderRadius: 4,
  },
  previewImg: {
    maxWidth: "100%",
    maxHeight: 460,
    backgroundColor: "#fff",
    border: "1px solid rgba(0,0,0,0.2)",
    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
  },
  previewWithToolbar: {
    display: "flex",
    gap: 12,
    alignItems: "stretch",
  },
  rightToolbar: {
    width: 90,
    display: "grid",
    gridTemplateColumns: "36px 36px",
    justifyItems: "center",
    alignItems: "start",
    alignContent: "start",
    padding: 6,
    gap: 6,
  },
}));

function sanitizeFilenameBase(name) {
  if (!name) return "Snapshot";
  return (
    String(name)
      .replace(/[\\/:*?"<>|]/g, "_")
      .trim() || "Snapshot"
  );
}

function formatTimestamp(d = new Date()) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}_${pad(
    d.getHours(),
  )}-${pad(d.getMinutes())}-${pad(d.getSeconds())}`;
}

function getLowercaseExtension(name) {
  const s = String(name || "");
  const base = s.split(/[\\/]/).pop() || "";
  const idx = base.lastIndexOf(".");
  if (idx < 0) return "";
  return base.slice(idx + 1).toLowerCase();
}

async function resolveSceneIndexForActiveFile(FV) {
  // Determine scene index: for multi-scene (.czi) use the actual scene; otherwise default to 1
  let sceneIndex = 1;
  try {
    let relPath = FV?.activeFilePath;
    if (!relPath && FV?.activeFileId) {
      const info = await Backend.getFileInfo(FV.activeFileId);
      relPath = info?.relativePath;
    }
    if (relPath && FV?.activeFileId) {
      const data = await Backend.getFileIdData(relPath);
      const list = Array.isArray(data?.sceneIds) ? data.sceneIds : [];
      const hit = list.find(
        (s) =>
          String(s?.id).toLowerCase() === String(FV.activeFileId).toLowerCase(),
      );
      if (hit && Number.isInteger(hit.scene))
        sceneIndex = Number(hit.scene) + 1;
    }
  } catch (_) {
    sceneIndex = 1;
  }
  return sceneIndex;
}

async function resolveCziSceneSuffix(FV) {
  // Only append scene suffix for CZI files
  const nameHint = FV?.activeFileName || FV?.activeFilePath || "";
  const ext = getLowercaseExtension(nameHint);
  if (ext !== "czi") return "";
  const sceneIndex = await resolveSceneIndexForActiveFile(FV);
  return `_S${sceneIndex}`;
}

function getScaleBarData(FV) {
  const r = FV?.rendererDict?.[FV.activeFileId];
  if (r?.state?.scaleBarData) return r.state.scaleBarData;
  if (r?.scaleBarData) return r.scaleBarData;
  return null;
}

const ScreenshotDialog = ({ open, onClose, fvOverride }) => {
  const classes = useStyles();
  const ctx = React.useContext(FileViewerContext);
  const FV = fvOverride || ctx;

  const [includeScaleBar, setIncludeScaleBar] = React.useState(
    FV.layoutStateObject?.scaleBar ?? true,
  );
  const [includeLabel, setIncludeLabel] = React.useState(
    FV.layoutStateObject?.slideLabel ?? false,
  );
  const [includeFileName, setIncludeFileName] = React.useState(false);
  const [includeUser, setIncludeUser] = React.useState(false);
  const [includeTime, setIncludeTime] = React.useState(false);
  const [scaleBarCorner, setScaleBarCorner] = React.useState("bottom-right");
  const [labelCorner, setLabelCorner] = React.useState("top-right");
  const [fileNameCorner, setFileNameCorner] = React.useState("top-left");
  const [userCorner, setUserCorner] = React.useState("bottom-left");
  const [timeCorner, setTimeCorner] = React.useState("bottom-left");
  const [fileNameBase, setFileNameBase] = React.useState("");
  const [previewSrc, setPreviewSrc] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const capturingRef = React.useRef(false);
  const [labelImg, setLabelImg] = React.useState(null);
  const [labelAvailable, setLabelAvailable] = React.useState(undefined);
  const [userText, setUserText] = React.useState("");
  const [userAvailable, setUserAvailable] = React.useState(undefined);

  React.useEffect(() => {
    if (!open || !FV?.activeFileId) return;
    let cancelled = false;
    const url = Backend.renderSlideLabel(FV.activeFileId);
    const img = new Image();
    img.onload = () => {
      if (cancelled) return;
      setLabelImg(img);
      setLabelAvailable(true);
    };
    img.onerror = () => {
      if (cancelled) return;
      setLabelImg(null);
      setLabelAvailable(false);
    };
    img.src = url;
    return () => {
      cancelled = true;
    };
  }, [open, FV?.activeFileId]);

  const makePreview = React.useCallback(async () => {
    if (!open) return;
    if (!FV?.rendererDict?.[FV.activeFileId]?.canvas) {
      setPreviewSrc(null);
      return;
    }
    if (capturingRef.current) return;
    capturingRef.current = true;
    try {
      const offscreen = await captureCanvasClone(FV);
      if (!offscreen) {
        setPreviewSrc(null);
        return;
      }
      const sbData = getScaleBarData(FV);
      const cropped = cropCanvasToNonBlack(offscreen, {
        scaleBarMaxWidth: sbData?.maxScaleWidth || sbData?.width || null,
      });
      const originalName =
        FV.activeFileName || FV.activeFilePath || String(FV.activeFileId || "");
      const justName = String(originalName).split(/[\\/]/).pop();
      const timeText = formatTimestamp();
      drawOverlaysStacked(cropped, {
        includeScaleBar,
        includeLabel,
        includeFileName,
        includeUser,
        includeTime,
        scaleBarCorner,
        labelCorner,
        fileNameCorner,
        userCorner,
        timeCorner,
        labelImg,
        scaleBarData: sbData,
        fileNameText: justName,
        userText,
        timeText,
      });
      setPreviewSrc(cropped.toDataURL("image/jpeg", 0.9));
    } finally {
      capturingRef.current = false;
    }
  }, [
    FV,
    open,
    includeScaleBar,
    includeLabel,
    includeFileName,
    includeUser,
    includeTime,
    labelImg,
    scaleBarCorner,
    labelCorner,
    fileNameCorner,
    userCorner,
    timeCorner,
    userText,
  ]);

  React.useEffect(() => {
    if (!open) return;
    // Load persisted dialog settings if present; otherwise fall back to current layout defaults
    let saved = null;
    try {
      saved = JSON.parse(localStorage.getItem(SS_SETTINGS_KEY) || "null");
    } catch (e) {
      saved = null;
    }
    setIncludeScaleBar(
      (saved && typeof saved.includeScaleBar === "boolean"
        ? saved.includeScaleBar
        : undefined) ??
        FV.layoutStateObject?.scaleBar ??
        true,
    );
    setIncludeLabel(
      (saved && typeof saved.includeLabel === "boolean"
        ? saved.includeLabel
        : undefined) ??
        FV.layoutStateObject?.slideLabel ??
        false,
    );
    setIncludeFileName(
      (saved && typeof saved.includeFileName === "boolean"
        ? saved.includeFileName
        : undefined) ?? false,
    );
    setIncludeUser(
      (saved && typeof saved.includeUser === "boolean"
        ? saved.includeUser
        : undefined) ?? false,
    );
    setIncludeTime(
      (saved && typeof saved.includeTime === "boolean"
        ? saved.includeTime
        : undefined) ?? false,
    );
    setScaleBarCorner(saved?.scaleBarCorner || "bottom-right");
    setLabelCorner(saved?.labelCorner || "top-right");
    setFileNameCorner(saved?.fileNameCorner || "top-left");
    setUserCorner(saved?.userCorner || "bottom-left");
    setTimeCorner(saved?.timeCorner || "bottom-left");
    let cancelled = false;
    (async () => {
      const base = (FV.activeFileName || FV.activeFilePath || "Snapshot")
        .split(/[\\/]/)
        .pop()
        .replace(/\.[^/.]+$/, "");
      let suffix = "";
      try {
        suffix = await resolveCziSceneSuffix(FV);
      } catch (e) {
        suffix = "";
      }
      if (cancelled) return;
      setFileNameBase(
        `${formatTimestamp()}_${sanitizeFilenameBase(base)}${suffix}`,
      );
    })();
    // Load current user for overlay text
    try {
      Backend.getCurrentUser((u) => {
        const txt =
          u?.fullName || u?.displayName || u?.user || u?.name || u?.email || "";
        setUserText(String(txt));
        setUserAvailable(Boolean(txt));
      });
    } catch (e) {
      setUserText("");
      setUserAvailable(false);
    }
    const id = requestAnimationFrame(() => makePreview());
    return () => {
      cancelled = true;
      cancelAnimationFrame(id);
    };
  }, [open, FV.activeFileId]);

  React.useEffect(() => {
    if (!open) return;
    const id = requestAnimationFrame(() => makePreview());
    return () => cancelAnimationFrame(id);
  }, [
    open,
    includeScaleBar,
    includeLabel,
    includeFileName,
    includeUser,
    includeTime,
    scaleBarCorner,
    labelCorner,
    fileNameCorner,
    userCorner,
    timeCorner,
    makePreview,
  ]);

  // Persist dialog settings when they change so they are restored on next open
  React.useEffect(() => {
    if (!open) return;
    const payload = {
      includeScaleBar,
      includeLabel,
      includeFileName,
      includeUser,
      includeTime,
      scaleBarCorner,
      labelCorner,
      fileNameCorner,
      userCorner,
      timeCorner,
    };
    try {
      localStorage.setItem(SS_SETTINGS_KEY, JSON.stringify(payload));
    } catch (e) {
      // ignore storage errors (e.g., quota)
    }
  }, [
    open,
    includeScaleBar,
    includeLabel,
    includeFileName,
    includeUser,
    includeTime,
    scaleBarCorner,
    labelCorner,
    fileNameCorner,
    userCorner,
    timeCorner,
  ]);

  // Temporarily hide the renderer's scale bar while the dialog is open without persisting to localStorage
  // Keep the setter stable across re-renders (e.g., VerticalToolBar recreates fvOverride objects)
  const setTempHideScaleBarRef = React.useRef(null);
  React.useEffect(() => {
    setTempHideScaleBarRef.current =
      FV && typeof FV.setTempHideScaleBar === "function"
        ? FV.setTempHideScaleBar
        : null;
  }, [FV]);
  React.useEffect(() => {
    const setter = setTempHideScaleBarRef.current;
    if (typeof setter !== "function") return;
    if (open) setter(true);
    return () => setter(false);
  }, [open]);

  const handleSave = async () => {
    if (!FV?.rendererDict?.[FV.activeFileId]?.canvas) return;
    if (capturingRef.current) return;
    setBusy(true);
    capturingRef.current = true;
    try {
      const offscreen = await captureCanvasClone(FV);
      if (!offscreen) return;
      const sbData = getScaleBarData(FV);
      const cropped = cropCanvasToNonBlack(offscreen, {
        scaleBarMaxWidth: sbData?.maxScaleWidth || sbData?.width || null,
      });
      const originalName =
        FV.activeFileName || FV.activeFilePath || String(FV.activeFileId || "");
      const justName = String(originalName).split(/[\\/]/).pop();
      const timeText = formatTimestamp();
      drawOverlaysStacked(cropped, {
        includeScaleBar,
        includeLabel,
        includeFileName,
        includeUser,
        includeTime,
        scaleBarCorner,
        labelCorner,
        fileNameCorner,
        userCorner,
        timeCorner,
        labelImg,
        scaleBarData: sbData,
        fileNameText: justName,
        userText,
        timeText,
      });
      const dataUrl = cropped.toDataURL("image/jpeg", 0.9);
      const link = document.createElement("a");
      const finalBase = sanitizeFilenameBase(fileNameBase);
      link.download = `${finalBase}.jpg`;
      link.href = dataUrl.replace("image/jpeg", "image/octet-stream");
      link.click();

      // Also save a copy to the central snapshots folder under either Viewer or Module/ID Project
      try {
        const baseNameOrig = String(
          FV.activeFileName || FV.activeFilePath || FV.activeFileId || "",
        )
          .split(/[\\/]/)
          .pop()
          .replace(/\.[^/.]+$/, "");

        const sceneIndex = await resolveSceneIndexForActiveFile(FV);

        // Choose destination context: if caller provided module/project details (e.g., VerticalToolBar), use them;
        // else if a case is active, save under Cases/[Case Acc#]; otherwise default to Viewer
        const hasModule =
          typeof FV?.moduleName === "string" && FV.moduleName.length > 0;
        const hasReadable =
          FV?.readableId !== undefined &&
          FV?.readableId !== null &&
          `${FV.readableId}`.length > 0;
        const hasProject =
          typeof FV?.projectName === "string" && FV.projectName.length > 0;

        const pickCaseAccForSave = () => {
          // Prefer the canonical accession key used in CaseList: data.case_acc
          const fromSel = FV?.selCase || null;
          if (fromSel?.case_acc) return String(fromSel.case_acc);
          try {
            const found = (FV?.caseList?.cases || []).find(
              (obj) => String(obj?.data?.Id) === String(FV.caseId),
            );
            if (found?.data?.case_acc) return String(found.data.case_acc);
          } catch (e) {
            // ignore lookup errors
          }
          return String(FV.caseId);
        };

        let moduleNameArg = "Viewer";
        let readableIdArg = 0;
        let projectNameArg = "";
        if (hasModule && hasReadable && hasProject) {
          moduleNameArg = FV.moduleName;
          readableIdArg = FV.readableId;
          projectNameArg = FV.projectName;
        } else if (FV?.caseId) {
          moduleNameArg = "Cases";
          projectNameArg = pickCaseAccForSave();
        }

        const blob = await fetch(dataUrl).then((r) => r.blob());
        Backend.saveSnapshot(
          moduleNameArg,
          readableIdArg,
          projectNameArg,
          baseNameOrig,
          sceneIndex,
          blob,
          (res) => {
            if (res?.savePath) {
              window.showSuccessSnackbar?.(
                `Snapshot saved to: ${res.savePath}`,
              );
            }
          },
        );
      } catch (e) {
        // non-fatal
        console.warn("Snapshot backend save failed", e);
      }
      onClose?.();
    } finally {
      capturingRef.current = false;
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Snapshot preview</DialogTitle>
      <DialogContent dividers>
        <Box className={classes.previewWithToolbar}>
          <Box flexGrow={1} className={classes.previewBox}>
            {previewSrc ? (
              <img
                src={previewSrc}
                alt="snapshot preview"
                className={classes.previewImg}
              />
            ) : (
              <Typography variant="body2" color="#ccc">
                {busy ? "Rendering preview…" : "No preview available."}
              </Typography>
            )}
          </Box>
          <Paper elevation={1} className={classes.rightToolbar}>
            <Tooltip disableInteractive placement="left" title="Scale Bar">
              <span>
                <IconButton
                  size="small"
                  onClick={() => setIncludeScaleBar((v) => !v)}
                  disabled={busy}
                  sx={{
                    color: includeScaleBar ? "primary.main" : "text.secondary",
                    width: 36,
                    height: 36,
                  }}
                >
                  <FontAwesomeIcon icon={faRuler} />
                </IconButton>
              </span>
            </Tooltip>
            <CornerButton
              value={scaleBarCorner}
              onChange={setScaleBarCorner}
              disabled={busy}
              title={`Scale Bar corner: ${scaleBarCorner}`}
            />

            <Tooltip
              disableInteractive
              placement="left"
              title={
                labelAvailable === false ? "Label (not available)" : "Label"
              }
            >
              <span>
                <IconButton
                  size="small"
                  onClick={() => setIncludeLabel((v) => !v)}
                  disabled={busy || labelAvailable === false}
                  sx={{
                    color:
                      includeLabel && labelAvailable !== false
                        ? "primary.main"
                        : "text.secondary",
                    width: 36,
                    height: 36,
                  }}
                >
                  <FontAwesomeIcon icon={faQrcode} />
                </IconButton>
              </span>
            </Tooltip>
            <CornerButton
              value={labelCorner}
              onChange={setLabelCorner}
              disabled={busy || labelAvailable === false}
              title={`Label corner: ${labelCorner}`}
            />

            <Tooltip disableInteractive placement="left" title="File Name">
              <span>
                <IconButton
                  size="small"
                  onClick={() => setIncludeFileName((v) => !v)}
                  disabled={busy}
                  sx={{
                    color: includeFileName ? "primary.main" : "text.secondary",
                    width: 36,
                    height: 36,
                  }}
                >
                  <FontAwesomeIcon icon={faInfo} />
                </IconButton>
              </span>
            </Tooltip>
            <CornerButton
              value={fileNameCorner}
              onChange={setFileNameCorner}
              disabled={busy}
              title={`File Name corner: ${fileNameCorner}`}
            />

            <Tooltip
              disableInteractive
              placement="left"
              title={userAvailable === false ? "User (not available)" : "User"}
            >
              <span>
                <IconButton
                  size="small"
                  onClick={() => setIncludeUser((v) => !v)}
                  disabled={busy || userAvailable === false}
                  sx={{
                    color:
                      includeUser && userAvailable !== false
                        ? "primary.main"
                        : "text.secondary",
                    width: 36,
                    height: 36,
                  }}
                >
                  <FontAwesomeIcon icon={faUser} />
                </IconButton>
              </span>
            </Tooltip>
            <CornerButton
              value={userCorner}
              onChange={setUserCorner}
              disabled={busy || userAvailable === false}
              title={`User corner: ${userCorner}`}
            />

            <Tooltip disableInteractive placement="left" title="Current time">
              <span>
                <IconButton
                  size="small"
                  onClick={() => setIncludeTime((v) => !v)}
                  disabled={busy}
                  sx={{
                    color: includeTime ? "primary.main" : "text.secondary",
                    width: 36,
                    height: 36,
                  }}
                >
                  <FontAwesomeIcon icon={faClock} />
                </IconButton>
              </span>
            </Tooltip>
            <CornerButton
              value={timeCorner}
              onChange={setTimeCorner}
              disabled={busy}
              title={`Time corner: ${timeCorner}`}
            />
          </Paper>
        </Box>

        <Box sx={{ mt: 2 }}>
          <TextField
            label="File name"
            value={fileNameBase}
            onChange={(e) => setFileNameBase(e.target.value)}
            fullWidth
            size="small"
            helperText={`.jpg will be appended`}
          />
        </Box>
        <Box sx={{ mt: 1 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block" }}
          >
            {(() => {
              const originalName =
                FV.activeFileName ||
                FV.activeFilePath ||
                String(FV.activeFileId || "");
              const base = String(originalName)
                .split(/[\\/]/)
                .pop()
                .replace(/\.[^/.]+$/, "");
              // Build destination segment:
              // - If provided via fvOverride: "[Module]/[ID] [Project]"
              // - Else if a case is open (?caseId=...): "Cases/[Case Acc#]"
              // - Else: "Viewer"
              const moduleName = FV?.moduleName;
              const readableId = FV?.readableId;
              const projectName = FV?.projectName;
              const hasModule =
                typeof moduleName === "string" && moduleName.length > 0;
              const hasReadable =
                readableId !== undefined &&
                readableId !== null &&
                `${readableId}`.length > 0;
              const hasProject =
                typeof projectName === "string" && projectName.length > 0;
              let destSegment = "Viewer";
              if (hasModule && hasReadable && hasProject) {
                destSegment = `${moduleName}/${readableId} ${projectName}`;
              } else if (FV?.caseId) {
                // Prefer the canonical accession key used in CaseList: data.case_acc
                const fromSel = FV?.selCase || null;
                let caseAcc = fromSel?.case_acc
                  ? String(fromSel.case_acc)
                  : null;
                if (!caseAcc) {
                  try {
                    const found = (FV?.caseList?.cases || []).find(
                      (obj) => String(obj?.data?.Id) === String(FV.caseId),
                    );
                    if (found?.data?.case_acc)
                      caseAcc = String(found.data.case_acc);
                  } catch (e) {
                    // ignore lookup errors
                  }
                }
                caseAcc = caseAcc || String(FV.caseId);
                destSegment = `Cases/${caseAcc}`;
              }
              return `Snapshot will be saved here as well: [Data Path]/snapshots/${destSegment}/Snapshot_0001_${base} S1.jpg`;
            })()}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 0.5 }}
          >
            Downloadsettings can be changed here: chrome://settings/downloads{" "}
            <a
              href="https://support.google.com/chrome/answer/95759"
              target="_blank"
              rel="noreferrer"
            >
              (learn more)
            </a>
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={busy || !previewSrc}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ScreenshotDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  fvOverride: PropTypes.object,
};

export default ScreenshotDialog;
